import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, CheckCircle2, XCircle, Eye, Upload, Search } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  "Birth Certificate", "Aadhaar", "Transfer Certificate", "Report Card", "Medical",
  "Address Proof", "Photo", "Caste Certificate", "Income Certificate", "Other",
];

const statusColor = (s) => ({
  verified: "border-emerald-300 text-emerald-700",
  rejected: "border-red-300 text-red-700",
  expired: "border-slate-400 text-slate-600",
  uploaded: "border-amber-300 text-amber-700",
  pending: "border-amber-300 text-amber-700",
}[s] || "border-slate-300 text-slate-600");

export default function Documents() {
  const { user } = useAuth();
  const isAdmin = ["school_admin", "super_admin"].includes(user?.role);
  const [students, setStudents] = useState([]);
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [f, setF] = useState({ student_id: "", doc_type: DOC_TYPES[0], file_url: "", remarks: "" });

  const load = async () => {
    try {
      const [d, s] = await Promise.all([
        api.get("/erp/documents"),
        isAdmin ? api.get("/students") : Promise.resolve({ data: [] }),
      ]);
      setDocs(d.data);
      setStudents(s.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const nameOf = (id) => {
    const s = students.find((x) => x.id === id);
    return s ? `${s.first_name} ${s.last_name}` : id?.slice(-6) || "—";
  };

  const submitDoc = async (e) => {
    e.preventDefault();
    if (!f.file_url) return toast.error("Please upload a file first");
    if (isAdmin && !f.student_id) return toast.error("Please select a student");
    try {
      await api.post("/erp/documents", f);
      toast.success("Document uploaded");
      setUploadOpen(false);
      setF({ student_id: "", doc_type: DOC_TYPES[0], file_url: "", remarks: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const verify = async (id, status) => {
    try {
      await api.put(`/erp/documents/${id}/verify?status=${status}`);
      toast.success(`Marked ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = useMemo(() => docs.filter((d) => {
    if (!q) return true;
    const hay = `${d.doc_type} ${nameOf(d.student_id)} ${d.status}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [docs, q, students]); // eslint-disable-line

  const stats = useMemo(() => ({
    total: docs.length,
    verified: docs.filter((d) => d.status === "verified").length,
    pending: docs.filter((d) => ["uploaded", "pending"].includes(d.status)).length,
    rejected: docs.filter((d) => d.status === "rejected").length,
  }), [docs]);

  return (
    <DashLayout variant={isAdmin ? "school" : (user?.role === "teacher" ? "teacher" : "parent")} title="Documents Vault">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Total" value={stats.total} color="bg-blue-50 text-blue-700" testId="doc-kpi-total" />
        <Kpi label="Verified" value={stats.verified} color="bg-emerald-50 text-emerald-700" testId="doc-kpi-verified" />
        <Kpi label="Pending" value={stats.pending} color="bg-amber-50 text-amber-700" testId="doc-kpi-pending" />
        <Kpi label="Rejected" value={stats.rejected} color="bg-red-50 text-red-700" testId="doc-kpi-rejected" />
      </div>

      <Card className="p-3 flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <Input placeholder="Search by student, type, status" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="doc-search" />
        </div>
        <Button className="bg-blue-700 hover:bg-blue-800" onClick={() => setUploadOpen(true)} data-testid="doc-upload-open">
          <Upload className="w-4 h-4 mr-1" /> Upload Document
        </Button>
      </Card>

      <Card className="p-0 overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Student</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Remarks</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((d) => (
                <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                  <TableCell className="font-semibold">{nameOf(d.student_id)}</TableCell>
                  <TableCell>{d.doc_type}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{(d.uploaded_at || "").slice(0, 10)}</TableCell>
                  <TableCell><Badge variant="outline" className={statusColor(d.status)}>{d.status}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{d.remarks || "—"}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => window.open(d.file_url, "_blank")} data-testid={`doc-view-${d.id}`}><Eye className="w-4 h-4" /></Button>
                    {isAdmin && d.status !== "verified" && <Button size="sm" onClick={() => verify(d.id, "verified")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`doc-verify-${d.id}`}><CheckCircle2 className="w-4 h-4" /></Button>}
                    {isAdmin && d.status !== "rejected" && <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => verify(d.id, "rejected")} data-testid={`doc-reject-${d.id}`}><XCircle className="w-4 h-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">No documents yet — click Upload above</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {filtered.map((d) => (
            <div key={d.id} className="p-4" data-testid={`doc-card-${d.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{nameOf(d.student_id)}</div>
                  <div className="text-xs text-slate-500">{d.doc_type} · {(d.uploaded_at || "").slice(0, 10)}</div>
                </div>
                <Badge variant="outline" className={statusColor(d.status)}>{d.status}</Badge>
              </div>
              {d.remarks && <div className="text-xs text-slate-500 mt-2">{d.remarks}</div>}
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => window.open(d.file_url, "_blank")} data-testid={`doc-card-view-${d.id}`}><Eye className="w-4 h-4 mr-1" /> View</Button>
                {isAdmin && d.status !== "verified" && <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => verify(d.id, "verified")} data-testid={`doc-card-verify-${d.id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Verify</Button>}
                {isAdmin && d.status !== "rejected" && <Button size="sm" variant="outline" className="flex-1 border-red-300 text-red-700" onClick={() => verify(d.id, "rejected")} data-testid={`doc-card-reject-${d.id}`}><XCircle className="w-4 h-4 mr-1" /> Reject</Button>}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-10 text-center text-slate-500 text-sm">No documents yet</div>}
        </div>
      </Card>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <form onSubmit={submitDoc} className="space-y-3" data-testid="doc-upload-form">
            {isAdmin && (
              <div>
                <Label className="mb-1 block text-xs">Student</Label>
                <Select value={f.student_id} onValueChange={(v) => setF({ ...f, student_id: v })}>
                  <SelectTrigger data-testid="doc-form-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · Class {s.class_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="mb-1 block text-xs">Document Type</Label>
              <Select value={f.doc_type} onValueChange={(v) => setF({ ...f, doc_type: v })}>
                <SelectTrigger data-testid="doc-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">File</Label>
              <FileUploader folder="documents" label="Upload File" onUploaded={(url) => setF({ ...f, file_url: url })} testId="doc-form-file" />
              {f.file_url && <div className="text-[10px] text-emerald-700 mt-1 truncate">✓ Uploaded: {f.file_url.slice(0, 60)}…</div>}
            </div>
            <div>
              <Label className="mb-1 block text-xs">Remarks (optional)</Label>
              <Input value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="doc-form-remarks" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-blue-700 hover:bg-blue-800" data-testid="doc-form-submit"><FileText className="w-4 h-4 mr-1" /> Save Document</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}

const Kpi = ({ label, value, color, testId }) => (
  <Card className="p-3" data-testid={testId}>
    <div className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${color}`}>{label}</div>
    <div className="text-2xl font-black mt-1 tabular-nums">{value}</div>
  </Card>
);
